import { useParams, useNavigate } from 'react-router';
import { ArrowLeft } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { MenuConfigurationOverride } from '@/components/application/menu-configuration/menu-configuration-override';
import { useCompany } from '@/hooks/api/use-companies';
import { useTranslation } from 'react-i18next';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';

export const CompanyMenuConfigPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:companies', 'common']);
    const { data: company, isLoading, error } = useCompany(id || '');

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <LoadingIndicator type="line-spinner" size="lg" />
                    <p className="text-tertiary mt-4">{t('menuConfig.loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !company) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Button
                        onClick={() => navigate('/admin/companies')}
                        size="sm"
                        color="secondary"
                        iconLeading={ArrowLeft}
                    >
                        {t('menuConfig.backToCompanies')}
                    </Button>
                </div>
                <div className="text-center py-12">
                    <p className="text-error-solid">{t('menuConfig.companyNotFound')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button
                        onClick={() => navigate(`/admin/companies/${id}/edit`)}
                        size="sm"
                        color="secondary"
                        iconLeading={ArrowLeft}
                    >
                        {t('menuConfig.back')}
                    </Button>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">
                            {t('menuConfig.title')}
                        </h1>
                        <p className="text-tertiary mt-1">
                            {t('menuConfig.subtitle', { companyName: company.company_name })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Company Info Card */}
            <div className="rounded-lg border border-secondary bg-primary p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-primary mb-1">
                            {company.company_name}
                        </h3>
                        <p className="text-sm text-tertiary">
                            {company.legal_name}
                        </p>
                        <p className="text-xs text-quaternary mt-1">
                            {t('form.fields.companyType')}: {t(`form.fields.companyTypes.${company.company_type}`)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Menu Configuration Override */}
            <MenuConfigurationOverride 
                companyId={id || ''} 
                companyType={company.company_type} 
            />
        </div>
    );
};

